import { useEffect, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useAuth } from '../../../lib/auth'
import { useGatedAction } from '../../../lib/useSubscription'
import { readError } from '../../../lib/errors'
import { fetchColorfulAiPlayers } from '../api/aiPlayers'
import { addColorfulAi, createColorfulRoom, joinColorfulRoomByCode, startColorful } from '../api/rooms'
import type { AiPlayerRow } from '../../../lib/database.types'
import { useT } from '../../../i18n'

interface Props {
  onBack: () => void
  /** Routes into the room screen — used by host, join, and quick play. */
  onHosted: (roomId: string) => void
  onOnlineMatch: () => void
  onPassPlay: () => void
  onOpenWeekly: () => void
  onOpenPaywall: () => void
}

const MIN_AI = 1
const MAX_AI = 5
const DEFAULT_AI = 3

/**
 * Colorful lobby, mirroring Three Step's option set exactly: party
 * host (create screen with the public/private toggle), join by code,
 * online match, pass play, weekly, and Quick Play vs AI. Quick Play
 * creates a REAL room (host + chosen AI seats) and routes into it,
 * the same as the other games — it used to run a client-side solo
 * engine, which meant backing out lost the game because there was no
 * room to keep (owner bug report, 2026-08-10).
 */
export function ColorfulLobbyScreen({
  onBack, onHosted, onOnlineMatch, onPassPlay, onOpenWeekly, onOpenPaywall,
}: Props) {
  const t = useT()
  const { profile, updateProfile } = useAuth()
  const gate = useGatedAction(onOpenPaywall)
  const [mode, setMode] = useState<'choose' | 'create' | 'join'>('choose')
  const [isPrivate, setIsPrivate] = useState(false)
  // Multi-select AI roster — show all personas at once, tap to toggle.
  // Same pattern Three Step + IET use after the consistency refactor.
  const [pool, setPool] = useState<AiPlayerRow[] | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [hosting, setHosting] = useState(false)
  const [joinCode, setJoinCode] = useState('')

  // Display name — defaults to the profile row's name but skips the
  // literal "Player" fallback (Apple Sign-In trigger default) so the
  // input visibly needs filling out instead of letting the user submit
  // as "Player."
  const initialName = profile?.display_name && profile.display_name !== 'Player'
    ? profile.display_name : ''
  const [nameDraft, setNameDraft] = useState<string>(initialName)
  useEffect(() => {
    if (profile?.display_name && profile.display_name !== 'Player') {
      setNameDraft(profile.display_name)
    }
  }, [profile?.display_name])

  useEffect(() => {
    let cancelled = false
    // Pull all active personas — the multi-select UI shows every one.
    fetchColorfulAiPlayers(50)
      .then(p => {
        if (cancelled) return
        setPool(p)
        setSelectedIds(prev => {
          if (prev.size > 0) return prev
          return new Set(p.slice(0, DEFAULT_AI).map(r => r.id))
        })
      })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  // Derived lineup: personas the user has actually selected, in pool order.
  const lineup = (pool ?? []).filter(p => selectedIds.has(p.id))

  function togglePersona(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        if (next.size <= MIN_AI) return prev
        next.delete(id)
      } else {
        if (next.size >= MAX_AI) return prev
        next.add(id)
      }
      return next
    })
  }

  const ready = pool !== null && lineup.length >= MIN_AI

  /**
   * Push the local name draft to `profiles.display_name` if it's
   * changed and non-empty. Runs before any action that will show the
   * host in the game — solo start, online host, or TV host.
   */
  async function persistNameIfChanged(): Promise<string> {
    const trimmed = nameDraft.trim()
    if (!trimmed) return profile?.display_name ?? 'You'
    if (trimmed === profile?.display_name) return trimmed
    try { await updateProfile({ display_name: trimmed }) }
    catch (e) { console.warn('[colorful-lobby] name update failed, continuing anyway', e) }
    return trimmed
  }

  /**
   * Quick Play: create a private room (strangers in the online queue
   * must never land in a solo-vs-AI table), seat the chosen AIs,
   * start it, and route in — identical shape to Three Step and IET.
   * Because it is a real room, backing out parks it in Jump back in
   * instead of losing the game.
   */
  async function start() {
    if (!ready || hosting) return
    setHosting(true); setError(null)
    try {
      await persistNameIfChanged()
      const room = await createColorfulRoom('online', true)
      for (const p of lineup) {
        await addColorfulAi(room.id, p.id)
      }
      await startColorful(room.id)
      onHosted(room.id)
    } catch (e) {
      setError(readError(e))
    } finally {
      setHosting(false)
    }
  }

  async function doCreate() {
    if (hosting) return
    setHosting(true); setError(null)
    try {
      await persistNameIfChanged()
      const room = await createColorfulRoom('online', isPrivate)
      onHosted(room.id)
    } catch (e) {
      setError(readError(e))
      setHosting(false)
    }
  }

  async function doJoin() {
    const code = joinCode.trim().toUpperCase()
    if (hosting || code.length !== 6) return
    if (!nameDraft.trim()) {
      setError(t('Type a name first. It shows up next to your seat.'))
      return
    }
    setHosting(true); setError(null)
    try {
      await persistNameIfChanged()
      const room = await joinColorfulRoomByCode(code)
      onHosted(room.id)
    } catch (e) {
      setError(readError(e))
    } finally {
      setHosting(false)
    }
  }

  // ─── Join mode ───────────────────────────────────────────────────────
  if (mode === 'join') {
    return (
      <div className="screen">
        <PageHeader title={t('Join Room')} onBack={() => { setMode('choose'); setError(null) }} />
        <div className="flex-1 px-5 py-8 space-y-6">
          <div className="text-center">
            <img src="/games/colorful.webp" alt="Colorful"
                 className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-card" />
            <h2 className="font-display text-xl font-bold text-brand-text">{t('Enter room code')}</h2>
            <p className="text-brand-muted text-sm mt-1">{t('Ask your host for the 6-character code')}</p>
          </div>
          <div>
            <label htmlFor="colorful-join-name"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Your name')}
            </label>
            <input
              id="colorful-join-name"
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value.slice(0, 24))}
              onBlur={() => { void persistNameIfChanged() }}
              maxLength={24}
              placeholder={t('Jodan')}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                         text-brand-text placeholder:text-brand-muted/60
                         font-display text-lg font-bold
                         focus:outline-hidden focus:border-game-colorful-dark"
            />
          </div>
          <div>
            <label htmlFor="colorful-join-code"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Room code')}
            </label>
            <input
              id="colorful-join-code"
              type="text"
              value={joinCode}
              onChange={e => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 6))}
              placeholder="ABC123"
              inputMode="text"
              autoCapitalize="characters"
              className="w-full px-4 py-4 rounded-2xl border border-brand-border bg-white
                         text-brand-text text-center text-2xl font-display font-bold
                         tracking-[0.2em] placeholder:text-brand-border
                         focus:outline-hidden focus:border-game-colorful-dark"
            />
          </div>
          {error && <p className="text-sm text-brand-danger text-center">{error}</p>}
          <button
            onClick={doJoin}
            disabled={hosting || joinCode.trim().length !== 6 || !nameDraft.trim()}
            className="btn-colorful disabled:opacity-40"
          >
            {hosting ? 'Joining…' : t('Join Room')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Create mode ─────────────────────────────────────────────────────
  if (mode === 'create') {
    return (
      <div className="screen">
        <PageHeader title={t('Create Room')} onBack={() => { setMode('choose'); setError(null) }} />
        <div className="flex-1 px-5 py-8 space-y-6">
          <div className="text-center">
            <img src="/games/colorful.webp" alt="Colorful"
                 className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-card" />
            <h2 className="font-display text-xl font-bold text-brand-text">{t('New room')}</h2>
            <p className="text-brand-muted text-sm mt-1">{t("You'll get a 6-character code to share.")}</p>
          </div>

          <label className="card p-4 flex items-center justify-between cursor-pointer">
            <div>
              <p className="font-semibold text-brand-text">{t('Private room')}</p>
              <p className="text-sm text-brand-muted mt-0.5">
                {t('Only people with the code can join.')}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isPrivate}
              onClick={() => setIsPrivate(v => !v)}
              className={`relative w-12 h-7 rounded-full transition-colors ${isPrivate ? 'bg-game-colorful-dark' : 'bg-brand-border'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-xs
                                transition-transform ${isPrivate ? 'translate-x-5' : ''}`} />
            </button>
          </label>

          {error && <p className="text-sm text-brand-danger text-center">{error}</p>}

          <button
            onClick={doCreate}
            disabled={hosting}
            className="btn-colorful disabled:opacity-40"
          >
            {hosting ? 'Creating…' : t('Create Room')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Choose mode ─────────────────────────────────────────────────────
  return (
    <div className="screen">
      <BackgroundTitle text="Colorful" color="#74705F" />
      <PageHeader
        title={t('Colorful')}
        subtitle={t('What color is sadness?')}
        onBack={onBack}
      />

      <div className="flex-1 flex flex-col px-5 py-5 gap-5 overflow-y-auto pb-36 relative z-10 glass-panes">
        {/* Your name — shows on the TV + everyone else's seat list.
            Saved to `profiles.display_name` right before any start or host
            action (see `persistNameIfChanged`). */}
        <div>
          <label htmlFor="colorful-name" className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
            {t('Your name')}
          </label>
          <input
            id="colorful-name"
            type="text"
            value={nameDraft}
            onChange={e => setNameDraft(e.target.value.slice(0, 24))}
            onBlur={() => { void persistNameIfChanged() }}
            maxLength={24}
            placeholder={t('Jodan')}
            className="w-full px-4 py-3 rounded-2xl border border-brand-border
                       bg-white text-brand-text placeholder:text-brand-muted/60
                       font-display text-lg font-bold
                       focus:outline-hidden focus:border-brand-primary focus:ring-2
                       focus:ring-brand-primary/20 transition-all"
          />
        </div>

        <div className="bg-game-colorful-light rounded-2xl shadow-card p-4
                        border-2 border-game-colorful-dark">
          <p className="text-xs font-semibold text-brand-text uppercase tracking-wide mb-2">
            {t('How it plays')}
          </p>
          <ul className="text-sm text-brand-text space-y-1.5">
            <li>{t('• Every round, someone sets a subject. Everyone picks a color.')}</li>
            <li>{t('• Match the majority to score. Unanimous rounds win for everyone.')}</li>
            <li>{t('• 5 rounds · ~5 min · base-category mode')}</li>
          </ul>
        </div>

        {/* Play with friends — promoted above the AI picker. The "Or"
            prefix moves to the AI section below per the cross-game
            consistency pass. */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">
            {t('Play with friends')}
          </p>

          <button
            onClick={() => gate(() => setMode('create'))}
            disabled={hosting}
            className="card w-full p-4 flex items-center gap-3 text-left
                       active:bg-brand-elevated transition-colors
                       disabled:opacity-60 disabled:cursor-wait"
          >
            <span className="text-2xl shrink-0">🎉</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-brand-text">{t('Party play with friends')}</p>
              <p className="text-xs text-brand-muted">{t('Host a room, public or private. Share a 6-character code so friends can join from their own phones.')}</p>
            </div>
          </button>

          <button
            onClick={() => { if (!hosting) setMode('join') }}
            disabled={hosting}
            className="card w-full p-4 flex items-center gap-3 text-left
                       active:bg-brand-elevated transition-colors
                       disabled:opacity-60 disabled:cursor-wait"
          >
            <span className="text-2xl shrink-0">🔑</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-brand-text">{t('Join a room')}</p>
              <p className="text-xs text-brand-muted">{t('Enter a 6-character code from a friend.')}</p>
            </div>
          </button>

          <button
            onClick={() => { if (!hosting) gate(onOnlineMatch) }}
            disabled={hosting}
            className="card w-full p-4 flex items-center gap-3 text-left
                       active:bg-brand-elevated transition-colors
                       disabled:opacity-60 disabled:cursor-wait"
          >
            <span className="text-2xl shrink-0">🌐</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-brand-text">{t('Online match')}</p>
              <p className="text-xs text-brand-muted">{t('Get matched with 3 other players. Take turns on your own time. The game waits for you.')}</p>
            </div>
          </button>

          <button
            onClick={() => { if (!hosting) gate(onPassPlay) }}
            disabled={hosting}
            className="card w-full p-4 flex items-center gap-3 text-left
                       active:bg-brand-elevated transition-colors
                       disabled:opacity-60 disabled:cursor-wait"
          >
            <span className="text-2xl shrink-0">📱</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-brand-text">{t('Phone Pass Play')}</p>
              <p className="text-xs text-brand-muted">{t('Local only. Pass the phone between people in the room. No internet needed.')}</p>
            </div>
          </button>

          <button
            onClick={() => { if (!hosting) onOpenWeekly() }}
            disabled={hosting}
            className="card w-full p-4 flex items-center gap-3 text-left
                       active:bg-brand-elevated transition-colors
                       disabled:opacity-60 disabled:cursor-wait"
          >
            <span className="text-2xl shrink-0">📆</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-brand-text">{t('Play the weekly')}</p>
              <p className="text-xs text-brand-muted">
                {t('One subject list for the whole community.')}
              </p>
            </div>
          </button>
        </div>

        {/* Or play with AI opponents — multi-select picker, sits
            below the friends section now. */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">
              {t('Or play with AI opponents')}
            </p>
            <p className="text-xs text-brand-muted">
              {lineup.length}/{MAX_AI} picked
            </p>
          </div>
          {error && (
            <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30 mb-2">
              <p className="text-sm text-brand-danger">{error}</p>
            </div>
          )}
          {pool === null ? (
            <div className="space-y-2">
              <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
              <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
              <div className="h-14 rounded-2xl bg-brand-elevated animate-pulse" />
            </div>
          ) : (
            <ul className="space-y-2">
              {pool.map(p => {
                const selected = selectedIds.has(p.id)
                const atCap = !selected && lineup.length >= MAX_AI
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => togglePersona(p.id)}
                      disabled={atCap}
                      aria-pressed={selected}
                      className={
                        'w-full p-3 flex items-center gap-3 rounded-2xl text-left ' +
                        'transition-colors active:scale-[0.99] ' +
                        (selected
                          ? 'bg-game-colorful-light border-2 border-game-colorful-dark shadow-card'
                          : 'bg-white border border-brand-border ') +
                        (atCap ? ' opacity-40' : '')
                      }
                    >
                      <span className="text-2xl shrink-0">{p.avatar_emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-brand-text text-sm">{p.name}</p>
                        <p className="text-xs text-brand-muted line-clamp-2">{t(p.persona)}</p>
                      </div>
                      <span
                        className={
                          'shrink-0 w-6 h-6 rounded-full flex items-center justify-center ' +
                          'text-sm font-bold border-2 transition-colors ' +
                          (selected
                            ? 'bg-game-colorful-dark border-game-colorful-dark text-white'
                            : 'bg-white border-brand-border text-brand-muted')
                        }
                        aria-hidden="true"
                      >
                        {selected ? '✓' : '+'}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="floating-actions">
        <button onClick={() => gate(start)} disabled={!ready} className="btn-primary disabled:opacity-40">
          {t('Quick play vs AI')}
        </button>
      </div>
    </div>
  )
}
